import { viewerStrings } from '../copy/strings.js';
import { message, CopyError } from '../copy/text.js';
import { z } from 'zod';
import { boundInput, queryExpressionSchema, orderSchema } from '@cdr-clarity/contracts';
import type { QueryCapabilities } from '@cdr-clarity/query-service';
import { predicateFields } from './query-references.js';

export const pageSizes = [25, 50, 100, 250, 500] as const;
const fieldIds = z.array(z.string().min(1).max(160)).max(1024);
export const viewPreferenceSchema = z.object({
  version: z.literal(1),
  platform: z.string(),
  catalogVersion: z.string(),
  visible: fieldIds,
  presetId: z.string().max(160),
  order: fieldIds,
  widths: z.record(z.string(), z.number().int().min(40).max(2000)),
  pinned: z.record(z.string(), z.enum(['left', 'right']).nullable()).optional(),
  sort: z.array(orderSchema).max(16),
  predicate: queryExpressionSchema.optional(),
  pageSize: z.number().refine((size) => (pageSizes as readonly number[]).includes(size)),
  timezone: z.string().max(100),
});
export type ViewPreference = z.infer<typeof viewPreferenceSchema>;
export type PreferenceStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export const preferenceKey = (platform: string): string => `cdr-clarity:view:${platform}:v1`;
export class InvalidViewPreferenceError extends Error {
  constructor(
    readonly storageKey: string,
    readonly serializedValue: string,
    cause: unknown,
  ) {
    super(
      message(viewerStrings.webStateViewPreferences.savedViewPreferencesAreInvalid, {
        value1: String(cause),
      }),
      { cause },
    );
    this.name = 'InvalidViewPreferenceError';
  }
}
const legacyPresets: Readonly<Record<string, string>> = {
  'basic-call-log': 'basic-logs',
  'call-troubleshooting': 'troubleshooting',
  'contact-center-supervision': 'contact-center',
  'find-a-call': 'find-call',
  'pstn-usage': 'pstn-usage',
};
const legacySchema = z.object({ visible: fieldIds, presetId: z.string() });

/** Remove only obsolete layout bindings, never an active predicate/sort or an unknown field. */
function migrateLayout(
  value: ViewPreference,
  capabilities: QueryCapabilities,
): { value: ViewPreference; changed: boolean } {
  const catalog = new Map(capabilities.fields.map((field) => [field.id, field]));
  const obsolete = (id: string): boolean => {
    if (!id.startsWith('primary.') && !id.startsWith('related.')) return false;
    const field = catalog.get(id.slice(id.indexOf('.') + 1));
    return (
      field !== undefined && !field.collections.includes(id.startsWith('primary.') ? 'cdr' : 'cmr')
    );
  };
  const order = value.order.filter((id) => !obsolete(id));
  const widths = Object.fromEntries(Object.entries(value.widths).filter(([id]) => !obsolete(id)));
  const pinned =
    value.pinned === undefined
      ? undefined
      : Object.fromEntries(Object.entries(value.pinned).filter(([id]) => !obsolete(id)));
  const migrated = { ...value, order, widths, ...(pinned === undefined ? {} : { pinned }) };
  return { value: migrated, changed: JSON.stringify(migrated) !== JSON.stringify(value) };
}

function validateAliases(value: ViewPreference, capabilities: QueryCapabilities): void {
  const catalog = new Map(capabilities.fields.map((field) => [field.id, field]));
  const qualified = (id: string) => id.startsWith('primary.') || id.startsWith('related.');
  if (value.visible.some((id) => !catalog.has(id) && qualified(id)))
    throw new CopyError(
      viewerStrings.webStateViewPreferences.globalFieldVisibilityMustUseCanonicalSource,
    );
  for (const id of [
    ...value.order,
    ...Object.keys(value.widths),
    ...Object.keys(value.pinned ?? {}),
    ...value.sort.map((item) => item.key),
    ...(value.predicate ? predicateFields(value.predicate) : []),
  ]) {
    if (catalog.has(id) || !qualified(id)) continue;
    const binding = id.startsWith('primary.') ? 'cdr' : 'cmr';
    const source = catalog.get(id.slice(id.indexOf('.') + 1));
    if (!source?.collections.includes(binding))
      throw new CopyError(
        message(viewerStrings.webStateViewPreferences.savedAssociationFieldIsUnavailableForIts, {
          value1: id,
        }),
      );
  }
}

export function readViewPreference(
  storage: PreferenceStorage,
  capabilities: QueryCapabilities,
  defaults: ViewPreference,
): { value: ViewPreference; notice: string | null } {
  const raw = storage.getItem(preferenceKey(capabilities.platform));
  if (raw !== null) {
    try {
      const input: unknown = JSON.parse(raw);
      boundInput(input);
      const migrated = migrateLayout(viewPreferenceSchema.parse(input), capabilities);
      const value = migrated.value;
      validateAliases(value, capabilities);
      if (value.platform !== capabilities.platform)
        throw new CopyError(
          viewerStrings.webStateViewPreferences.savedViewBelongsToADifferentPlatform,
        );
      new Intl.DateTimeFormat('en', { timeZone: value.timezone });
      return {
        value: { ...value, catalogVersion: capabilities.catalogVersion },
        notice: migrated.changed
          ? viewerStrings.webStateViewPreferences.savedColumnLayoutAdjustedObsoleteSourceBindings
          : value.catalogVersion === capabilities.catalogVersion
            ? null
            : viewerStrings.webStateViewPreferences.fieldCatalogChangedSavedSelectionsWereRetained,
      };
    } catch (cause) {
      throw new InvalidViewPreferenceError(preferenceKey(capabilities.platform), raw, cause);
    }
  }
  const legacy =
    capabilities.platform === 'cucm'
      ? storage.getItem('cdr-clarity:lab:cucm:field-visibility:v1')
      : null;
  if (legacy !== null) {
    try {
      const input: unknown = JSON.parse(legacy);
      boundInput(input);
      const selection = legacySchema.parse(input);
      return {
        value: {
          ...defaults,
          visible: [...new Set(selection.visible)],
          presetId: legacyPresets[selection.presetId] ?? 'custom',
        },
        notice: viewerStrings.webStateViewPreferences.savedFieldSelectionImportedFromTheDesign,
      };
    } catch (cause) {
      throw new InvalidViewPreferenceError(
        'cdr-clarity:lab:cucm:field-visibility:v1',
        legacy,
        cause,
      );
    }
  }
  return { value: defaults, notice: null };
}

export function saveViewPreference(
  storage: PreferenceStorage,
  value: ViewPreference,
  capabilities?: QueryCapabilities,
): void {
  boundInput(value);
  const valid = viewPreferenceSchema.parse(value);
  if (capabilities) validateAliases(valid, capabilities);
  new Intl.DateTimeFormat('en', { timeZone: valid.timezone });
  storage.setItem(preferenceKey(valid.platform), JSON.stringify(valid));
}
